import { eventBus } from '@/Mixins/eventbus.js';
import { format, toZonedTime } from 'date-fns-tz';
import isThisWeek from 'date-fns/isThisWeek';
import isThisYear from 'date-fns/isThisYear';
import isToday from 'date-fns/isToday';
import isYesterday from 'date-fns/isYesterday';
import { createPopper } from '@popperjs/core';
import CryptoJS from 'crypto-js';
import { USER_ROLE_IDS } from '@/Shared/constants.js';
import { useUserStore } from '@/stores/useUserStore.js';

const appTooltips = {};

export const globalMixins = {
    getAuthUserId () {
        const userStore = useUserStore();
        return Number(userStore.currentUser?.id ?? null);
    },

    getHumanDate: function (date, showTimeZone = false) {
        // Validate date input to prevent "Invalid time value" errors
        if (!date) {
            return '';
        }

        // Convert to Date object if it's a string
        const dateObj = date instanceof Date ? date : new Date(date);

        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            console.warn('Invalid date passed to getHumanDate:', date);
            return '';
        }

        // If the date is today: "Today at [Hour]"
        // If the date is yesterday: "Yesterday at [Hour]"
        // If the date is within the current week (excluding today and yesterday): "[Day of the week] at [Hour]"
        // If the date is not within the current week: "[Month] [Day] at [Hour]"
        // If the date is from last year: "[Month] [Day], [Year] at [Hour]"

        try {
            const startsWith = isToday (dateObj) ? `'Today'` :
                isYesterday (dateObj) ? `'Yesterday'` :
                    isThisWeek (dateObj) ? 'cccc' :
                        isThisYear (dateObj) ? 'MMMM dd' : `MMMM dd',' yyyy`;

            return format(dateObj, startsWith + ' \'at\' h\':\'mm aa' + (showTimeZone ? ' (zz)' : ''));
        } catch (error) {
            console.warn('Error formatting date in getHumanDate:', error, date);
            return '';
        }
    },

    getFormattedDate (date, prefomattedDate = false, hideYear = false, showTime = false) {
        const MONTH_NAMES = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        const day = date.getDate ();
        const month = MONTH_NAMES[date.getMonth ()];
        const year = date.getFullYear ();
        const timeSuffix = showTime
            ? ` at ${date.toLocaleString ('en-US', { hour: 'numeric', hour12: true })}` // e.g. " at 5 PM"
            : '';

        if (prefomattedDate) {
            return `${prefomattedDate} at ${date.toLocaleString ('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            })}`;
        }

        if (hideYear) {
            return `${day} ${month}${timeSuffix}`;
        }

        return `${day} ${month}, ${year}${timeSuffix}`;
    },

    getHumanDateAsana (dateParam, withTime = false) {
        if (!dateParam) {
            return null;
        }

        const date = toZonedTime (dateParam, 'America/Chicago');
        const DAY_IN_MS = 86400000; // 24 * 60 * 60 * 1000
        const today = toZonedTime (new Date (), 'America/Chicago');
        const yesterday = new Date (today - DAY_IN_MS);
        const seconds = Math.round ((today - date) / 1000);
        const minutes = Math.round (seconds / 60);
        const hours = Math.round (minutes / 60);
        const days = Math.round (hours / 24);
        const isToday = today.toDateString () === date.toDateString ();
        const isYesterday = yesterday.toDateString () === date.toDateString ();
        const isThisMonth = today.getMonth () === date.getMonth ();
        const isThisYear = today.getFullYear () === date.getFullYear ();

        if (seconds < 90) {
            return 'about a minute ago';
        } else if (minutes < 60) {
            return `${minutes} minutes ago`;
        } else if (isToday) {
            if (hours === 1) {
                return `1 hour ago`;
            }
            return `${hours} hours ago`;
        } else if (isYesterday) {
            return this.getFormattedDate (date, 'Yesterday');  // Yesterday at 10:20
        } else if (isThisYear) {
            if (isThisMonth) {
                const timeSuffix = withTime
                    ? ` at ${date.toLocaleString ('en-US', { hour: 'numeric', hour12: true })}` // e.g. " at 5 PM"
                    : '';
                return `${days} days ago${timeSuffix}`;   // 5 days ago at 5 PM
            }
            return this.getFormattedDate (date, false, true, withTime); // 18 Aug (at 5 PM)
        }
        return this.getFormattedDate (date, false, false, withTime); // 10 Jan, 2017 (at 5 PM)
    },

    renderLimitedList: function (lengthList = 2, list) {
        if (list.length >= lengthList) {
            return list.slice (0, lengthList);
        }

        return list;
    },

    getValidationState (formField, errorField, successFieldLength = 2) {
        if (errorField && !formField) {
            return false
        }
        if (!formField) {
            return null
        }
        if (formField && formField.length && formField.length > successFieldLength) {
            return true
        }
    },

    isMarketscaleDomain (user) {
        return user.email.includes ('@marketscale.com');
    },

    hasPermission (permission) {
        // add potential override for cont:upd-org
        if (permission === 'cont:upd-org') {
            if (this.$page.props.permissions.includes('cont:upd-any')) {
                return true;
            }
        }
        // console.log("permissions", this.$page.props.permissions);
        return this.$page.props.permissions.includes(permission);
    },

    hasWriteAccessToContentApproval(contentable, userId = null) {
        const authUserId = Number(userId ?? this.getAuthUserId());

        if (this.hasPermission('cont:upd-any') || this.isSystemAdmin()) {
            return true;
        }
        const approvalUsers = (contentable.approval_users || [])
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id));
        const hasGroups = (contentable?.content?.groups || []).length > 0;

        // The media_approvers tier only governs recorded/uploaded content.
        const isApprovableMedia = contentable.model_type === 'MediaFile'
            || contentable.model_type === 'BulkMedia'
            || contentable.model_type === 'PodcastingRecord';

        if (isApprovableMedia) {

            if (this.hasPermission('appr:org')) {
                if (approvalUsers.length > 0 && hasGroups) {
                    return approvalUsers.includes(authUserId);
                }
                return true;
            }

            if (approvalUsers.length > 0 && approvalUsers.includes(authUserId)) {
                return true;
            }

            // Room/show creators can always approve recordings from their rooms
            if (contentable.model_type === 'PodcastingRecord') {
                const roomCreatorId = Number(contentable.room_creator_id || contentable.podcasting?.user_id);
                if (roomCreatorId && roomCreatorId === authUserId) {
                    return true;
                }
            }

            if (approvalUsers.length === 0 && !hasGroups && useUserStore().canBeApprover) {
                return true;
            }
        }

        // Owner self-approval must still honour the channel's media_approvers
        // tier for recorded/uploaded media — canBeApprover encodes the tier for
        // the user's current org. Without this a standard user could approve
        // their own upload in an admins-only channel (mirrors Security.php).
        if (((this.hasPermission('appr:own') && !hasGroups) || (hasGroups && approvalUsers.length === 0))
            && (!isApprovableMedia || useUserStore().canBeApprover)) {
            const contentUserId = contentable.user_id || contentable.content?.user_id;
            return Number(contentUserId) === authUserId;
        }

        return false;
    },

    isSystemAdmin () {
        return this.$page.props.user.is_super || this.hasPermission('cont:upd-any');
    },

    getSafariVersion () {
        if (navigator.userAgent.includes ('Version/')) {
            const [version, patch] = navigator.userAgent.split ('Version/')[1].split ('.');

            if (version && patch[0]) {
                return version + '.' + patch[0];
            }
        }
        return null;
    },

    isSafariBrowser () {
        return !!(
            !navigator.userAgent.match (/Chrome/)
            && !navigator.userAgent.match (/CriOS/)
            && navigator.userAgent.match (/Safari/)
        );
    },

    isSafariDesktop () {
        return this.isSafariBrowser () && !this.isMobileOrTabletSafari ();
    },

    isMobileOrTabletSafari () {
        return !!(this.isSafariBrowser () && this.isIOS () && !!navigator.userAgent.match (/AppleWebKit/));
    },

    isIOS () {
        return !!navigator.userAgent.match (/(iPod|iPhone|iPad)/);
    },

    isIPadPro () {
        // /Macintosh/.test(navigator.userAgent) && 'ontouchend' in document;
        return !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test (navigator.platform));
    },

    isMacOrIOS () {
        return !!navigator.userAgent.match (/(Mac|iPod|iPhone|iPad)/)
    },

    getIOSChromeVersion () {
        if (navigator.userAgent.includes ('CriOS/')) {
            const [version, patch] = navigator.userAgent.split ('CriOS/')[1].split ('.');

            if (version && patch[0]) {
                return version + '.' + patch[0];
            }
        }
        return null;
    },

    isChromeIOS () {
        return !!navigator.userAgent.match ('CriOS');
    },

    isChrome () {
        return !!navigator.userAgent.match (/Chrom(e|ium)\/([0-9]+)\./);
    },

    isFirefox () {
        return !!navigator.userAgent.toLowerCase ().match (/firefox/);
    },

    getFirefoxVersion () {
        const parts = navigator.userAgent.match (/Firefox\/([0-9]+)\./);
        return parts ? parseInt (parts[1], 10) : false;
    },

    isMobileOrTabletDevice () {
        return !!navigator.userAgent.match (/(Android|iPod|iPhone|iPad)/);
    },

    isAndroid () {
        return !!navigator.userAgent.match (/Android/);
    },

    async getPermissionStatus (permissionName, getUpdatedStatus) {
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const status = await navigator.permissions.query ({name: permissionName});
                if (getUpdatedStatus) {
                    status.onchange = function () {
                        getUpdatedStatus (permissionName, this.state)
                    };
                }
                return status.state || null;
            } catch (e) {
                return null;
            }
        }
        return null;
    },

    async getPermissionDeniedMessage (err) {
        const statusCamera = await this.getPermissionStatus ('camera');
        const statusMicrophone = await this.getPermissionStatus ('microphone');

        if (statusCamera && statusCamera === 'denied') {
            return 'Studio was denied access to your camera by your browser. Please reload the page and enable permissions to record your content.';
        } else if (statusMicrophone && statusMicrophone === 'denied') {
            return 'Studio was denied access to your microphone by your browser. Please reload the page and enable permissions to record your content.';
        } else if (err && err.toString ().toLowerCase ().includes ('permission denied')) {
            return 'Studio was denied access to your microphone and camera by your browser. Please reload the page and enable permissions to record your content.';
        }

        return '';
    },

    onCopyToClipboard (value, title = 'Link copied to clipboard') {
        try {
            if (!value) {
                eventBus.emit('AlertModal', {
                    title: `Something went wrong`,
                    text: `Oops, unable to copy. Value is empty!`,
                    showIcon: 'yellow',
                    button2: 'OK',
                });
                throw 'Value is empty!';
            }

            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                // For Safari and iOS Safari, fall back to execCommand if clipboard API fails
                const copyUsingClipboardAPI = () => {
                    return navigator.clipboard.writeText(value)
                        .then(() => {
                            this.$notify({group: 'notification', title: title});
                        })
                        .catch((err) => {
                            // If clipboard API fails, try fallback method
                            this.copyUsingExecCommand(value, title);
                        });
                };

                if (this.isSafariBrowser()) {
                    setTimeout(copyUsingClipboardAPI, 100);
                } else {
                    copyUsingClipboardAPI();
                }
            } else {
                // Fallback for older browsers
                this.copyUsingExecCommand(value, title);
            }
        } catch (err) {
            eventBus.emit('AlertModal', {
                title: `Something went wrong`,
                text: `Oops, unable to copy.`,
                showIcon: 'yellow',
                button2: 'OK',
            });
        }
    },

    copyUsingExecCommand(value, title) {
        const textarea = document.createElement('textarea');
        textarea.className = 'temp-textarea';
        textarea.style.opacity = '0';
        textarea.style.position = 'fixed';
        textarea.style.left = '0';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.value = value;
        textarea.select();
        textarea.focus();

        try {
            document.execCommand('copy');
            this.$notify({group: 'notification', title: title});
        } catch (err) {
            eventBus.emit('AlertModal', {
                title: `Something went wrong`,
                text: `Oops, unable to copy.`,
                showIcon: 'yellow',
                button2: 'OK',
            });
        } finally {
            textarea.remove();
        }
    },

    getSizeInMB (size) {
        return (size / 1024 / 1024).toFixed (2);
    },

    // onBlurLayout() {
    //     let modalShow = document.querySelector(".c-BlurLayout.show");
    //     if (modalShow) {
    //         document.querySelector(".o-app-layout").style.filter = 'blur(5px)';
    //     }
    // },
    // onBlurLayoutRemove() {
    //     const layout = document.querySelector(".o-app-layout");
    //     if (layout) {
    //         layout.style.filter = 'none';
    //     }
    // },

    onHistoryBack (url = null, refresh = false) {
        if (history.length > 2) {
            let prevPage = window.location.href;
            history.back ();

            if (refresh) {
                setTimeout (() => {
                    this.$inertia.get (window.location.href);
                }, 100)
            }

            setTimeout (() => {
                if (window.location.href == prevPage) {
                    this.$inertia.get (this.route ('dashboard'))
                }
            }, 500);
        } else if (url) {
            this.$inertia.get (url);
        } else {
            this.$inertia.get (this.route ('dashboard'))
        }
    },

    resetFocus (id) {
        document.activeElement.blur ();
        let parent = document.getElementById (id);
        let input = document.createElement ('input');
        input.style.position = 'fixed';
        input.setAttribute ('type', 'text');
        if (parent) {
            parent.insertBefore (input, parent.firstChild);
        }
        input.focus ();
        if (parent) {
            parent.removeChild (input);
        }
    },

    toggleHideDropdownByContainerId (id, styleValue) {
        const dropdown = document.querySelector (`#${id} .ti-autocomplete`);

        if (dropdown) {
            dropdown.style.display = styleValue;
        }
    },

    toggleDropdown ({id, items, hideWhenCountEqual = 1, removeListener = false} = {}) {
        if (removeListener) {
            return this.openDropdownOnFocus (id, true);
        }
        // console.log('toggle dropdown')
        // if ([items].length === hideWhenCountEqual) {
        //     this.toggleHideDropdownByContainerId(id, 'none');
        //     this.resetFocus(id);
        //     this.openDropdownOnFocus(id);
        // }
        // if ([items].length === 0) {
        //     this.toggleHideDropdownByContainerId(id, 'block');
        // }

        this.toggleHideDropdownByContainerId (id, 'none');
        this.resetFocus (id);
        this.openDropdownOnFocus (id);
    },

    openDropdownOnFocus (id, remove = false) {
        const input = document.querySelector (`#${id} .ti-new-tag-input`);
        const handler = () => {
            const dropdown = document.querySelector (`#${id} .ti-autocomplete`);
            if (dropdown) {
                dropdown.style.display = 'block';
            }
        };
        if (input && remove) {
            return input.removeEventListener ('focus', handler)
        }
        if (input) {
            input.addEventListener ('focus', handler)
        }
    },

    getColorDependsOnBackground (color) {
        if (color) {
            const rgbValueArr = this.hexToRgb (color);

            const colorValue = Math.round (((parseInt (rgbValueArr[0]) * 299) +
                (parseInt (rgbValueArr[1]) * 587) +
                (parseInt (rgbValueArr[2]) * 114)) / 1000);

            return (colorValue > 125) ? 'black' : 'white';
        }
        return '';
    },

    // getColorDependsOnBrandBackground(color) { /*NOTE: if bg color is color of the brand */
    //     let colorVal = color ? color : getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    //     return this.getColorDependsOnBackground(colorVal);
    // },

    hexToRgb (hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec (hex);
        return result ? [
            parseInt (result[1], 16),
            parseInt (result[2], 16),
            parseInt (result[3], 16)
        ] : null;
    },

    isDevMode () {
        return this.$page.props?.appEnv === 'sandbox' || this.$page.props?.appEnv === 'local';
    },

    isUserImg (url) {
        return !!(url?.length && /\.(jpeg?|jpg?|jpe?|png?|heic?|heif?|bmp?|svg?|webp?)$/i.test (url));
    },

    onOpenSubmissionIssueModal (onSubmitAgainFun = () => {}) {
        eventBus.emit ('AlertModal', {
            title: `SUBMISSION ISSUE`,
            text: `We've saved your progress, but it seems there's a technical issue preventing the submission of your recording. <br><br> If the problem persists, please don't hesitate to contact our <a class="c-alert-modal__link" href="https://help.marketscale.com" target="_blank">Customer Support</a>`,
            showIcon: 'red',
            button1: 'Close',
            button2: 'Submit again',
            onButton2Click: () => onSubmitAgainFun (),
        });
    },

    onShowTooltip (targetId, placement = 'top', modifiers = []) {
        const buttonElement = document.getElementById (targetId);
        const tooltipElement = document.getElementById (`tooltip-for-${targetId}`);

        if (buttonElement && tooltipElement) {
            // tooltipElement.setAttribute('data-show', '');
            tooltipElement.classList.add ('show');

            appTooltips[targetId] = createPopper (buttonElement, tooltipElement, {
                // createPopper(buttonElement, tooltipElement, {
                placement: placement,
                modifiers: modifiers,
            });

            appTooltips[targetId]?.update ()
        }
    },

    onHideTooltip (targetId) {
        appTooltips[targetId]?.destroy ();
        const tooltipElement = document.getElementById (`tooltip-for-${targetId}`);
        if (tooltipElement) {
            // tooltipElement.removeAttribute('data-show');
            tooltipElement.classList.remove ('show');
        }
    },

    onDropdownHide (dropdownBtnId) {
        let dropdown = new this.$dropdown(document.getElementById(dropdownBtnId));
        if (dropdown) {
            dropdown.hide();
        }
    },

    triggerElementClickById(elementId) {
        document.getElementById(elementId).click();
    },

    generateUserHash(param) {
        const secretKey = this.$page.props?.appEnv === 'production' ? 'wdFdLd08OD0B_Qlz4_I6edUaPNgTGlKhr1iaKLxX' : 'A7IfgDNWyOOD7bI1bc3jepOqRz2_VVl76NjSgogC';
        const userIdentifier = param; // IMPORTANT: the email for your user
        const hash = CryptoJS.HmacSHA256(userIdentifier, secretKey).toString(CryptoJS.enc.Hex);
        return hash;
    },

    // Helper function to reliably detect file MIME type
    getFileMimeType(file) {
        // First try the direct type property
        if (file.type && file.type.trim() !== '') {
            return file.type;
        }

        // Try the nested file.type property (for filestack)
        if (file.file?.type && file.file.type.trim() !== '') {
            return file.file.type;
        }

        // Fallback to extension-based detection
        const fileName = file.name ?? file.file?.name ?? '';
        const extension = fileName.split('.').pop()?.toLowerCase();

        const mimeTypeMap = {
            'mkv': 'video/x-matroska',
            'mp4': 'video/mp4',
            'avi': 'video/x-msvideo',
            'mov': 'video/quicktime',
            'wmv': 'video/x-ms-wmv',
            'flv': 'video/x-flv',
            'webm': 'video/webm',
            'm4v': 'video/x-m4v',
            '3gp': 'video/3gpp',
            'mp3': 'audio/mpeg',
            'wav': 'audio/wav',
            'aac': 'audio/aac',
            'flac': 'audio/flac',
            'ogg': 'audio/ogg',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
            'pdf': 'application/pdf',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'txt': 'text/plain',
            'zip': 'application/zip',
            'rar': 'application/x-rar-compressed'
        };

        return mimeTypeMap[extension] || 'application/octet-stream';
    },

    // Handle image loading errors with retry mechanism
    // Fixes Safari issue where successfully loaded images get replaced by fallback
    handleImageError(event, maxRetries = 3) {
        const img = event.target;

        // Ignore if image already loaded successfully (prevents Safari from replacing loaded images)
        if (img.dataset.loaded === 'true' || (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0)) return;

        if (img.dataset.retrying === 'true') return;

        // Get or store original source
        if (!img.dataset.originalSrc) {
            const src = img.getAttribute('data-initial-src') || img.src || '';
            const cleanSrc = src.split('?')[0].split('&_retry=')[0].split('&_t=')[0];

            // Only retry valid URLs (not fallback, data URIs, or blobs)
            if (!cleanSrc || cleanSrc.includes('processing_media2.png') || cleanSrc.includes('data:') || cleanSrc.startsWith('blob:')) return;

            img.dataset.originalSrc = cleanSrc;
        }

        const originalSrc = img.dataset.originalSrc;
        const retryCount = parseInt(img.dataset.retryCount || '0');

        // Show fallback after max retries
        if (retryCount >= maxRetries) {
            img.src = '/img/video/processing_media2.png';
            img.style.opacity = '0.7';
            img.alt = 'Image unavailable';
            return;
        }

        // Setup retry
        img.dataset.retrying = 'true';
        img.dataset.retryCount = (retryCount + 1).toString();
        img.style.opacity = '0.5';

        const delay = this.isSafariBrowser() ? 1000 * (retryCount + 1) : 500 * (retryCount + 1);

        img._retryTimeoutId = setTimeout(() => {
            // Abort if image loaded in the meantime
            if (img.dataset.loaded === 'true' || (img.complete && img.naturalWidth > 0)) {
                img.dataset.retrying = 'false';
                img._retryTimeoutId = null;
                return;
            }

            // Retry: Safari first attempt uses original URL, others use cache-busting
            if (this.isSafariBrowser() && retryCount === 0) {
                img.removeAttribute('loading');
                img.src = originalSrc;
            } else {
                const separator = originalSrc.includes('?') ? '&' : '?';
                img.src = `${originalSrc}${separator}_retry=${retryCount + 1}&_t=${Date.now()}`;
            }

            // Clear retry flag after delay
            setTimeout(() => {
                img.dataset.retrying = 'false';
                img._retryTimeoutId = null;
            }, delay + 100);
        }, delay);
    }
};
